// apps/client/src/ui/screens/settings.tsx (M1-T18, spec §8.5, §10.4, §11 "contact") — volumes, vibration, notifications + quiet hours,
// graphics quality, playing full screen (2026-10-15: ui/install.tsx), account (M2-T08: blocked players, delete account), credits/licences
// and help. Device settings live in ClientSettings (localStorage); notifications go through the notifications slice.
import { t as tr, type KitLicence, type StringKey } from '@whiskerton/content';
import { useEffect, useState, type ReactNode } from 'react';
import { useCommands } from '../../game/instance';
import type { ClientSettings } from '../../state/slices/core';
import { notificationPermission } from '../../state/slices/notifications';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { InstallSettings } from '../install';
import { Button, Panel, Tabs } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { settings: undefined } }

const STEP = 30;                                                     // quiet-hour pickers move in half hours
const QUIET_OPTIONS = Array.from({ length: 1440 / STEP }, (_, i) => i * STEP);
const QUALITIES: readonly { id: ClientSettings['quality']; label: StringKey }[] = [
  { id: 'auto', label: 'settings.qualityAuto' }, { id: 'low', label: 'settings.qualityLow' }, { id: 'high', label: 'settings.qualityHigh' }];
/** '10:30 PM' from minutes of day; hours reach text only through params (rev 6). */
export const minuteText = (m: number): string => {
  const h = Math.floor(m / 60);
  return tr('settings.quietTime', { h: h % 12 || 12, mm: String(m % 60).padStart(2, '0'), ampm: tr(h < 12 ? 'time.am' : 'time.pm') });
};
/** The address inside settings.contact, so the mailto link and the text never disagree. */
const contactAddress = (text: string): string | null => /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/.exec(text)?.[0] ?? null;

const sectionStyle = { display: 'grid', gap: 10, alignContent: 'start' } as const;
const rowStyle = { display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', fontWeight: 800 } as const;
const inputStyle = { accentColor: 'var(--leaf-700)' } as const;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <section style={sectionStyle}><strong style={{ fontWeight: 900, fontSize: 18 }}>{title}</strong>{children}</section>;
}
function Toggle({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={rowStyle}>
      <span>{label}</span>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)}
        style={{ ...inputStyle, width: 26, height: 26, flex: 'none' }} />
    </label>
  );
}
function Slider({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label style={{ display: 'grid', gap: 4, fontWeight: 800 }}>
      <span>{label}</span>
      <input type="range" min={0} max={1} step={0.05} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ ...inputStyle, width: '100%' }} />
    </label>
  );
}
function MinutePicker({ label, value, onChange }: { label: string; value: number; onChange: (m: number) => void }) {
  return (
    <label style={{ display: 'grid', gap: 4, fontWeight: 800 }}>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))}
        style={{ font: 'inherit', fontWeight: 800, borderRadius: 12, border: 0, padding: '8px 10px', background: 'var(--cream-100)', color: 'var(--wood-800)' }}>
        {QUIET_OPTIONS.map((m) => <option key={m} value={m}>{minuteText(m)}</option>)}
      </select>
    </label>
  );
}

function Notifications() {
  const t = useT();
  const supported = useGame((s) => s.notificationsSupported);
  const enabled = useGame((s) => s.notificationsEnabled);
  const quiet = useGame((s) => s.notificationsQuiet);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(() => notificationPermission() === 'denied');
  const fail = () => useGame.getState().toast('errors.server_error', undefined, 'warn');
  const toggle = async (on: boolean) => {
    setBusy(true);
    try {
      const s = useGame.getState();
      if (!on) await s.notificationsDisable();
      else if (!(await s.notificationsEnable())) setDenied(notificationPermission() === 'denied');
    } catch { fail(); } finally { setBusy(false); }
  };
  const setQuiet = (start: number, end: number) => { void useGame.getState().notificationsSetQuiet(start, end).catch(fail); };
  if (!supported) return <p style={{ margin: 0 }}>{t('settings.notificationsUnavailable')}</p>;
  return (
    <>
      <Toggle label={t('settings.notificationsOn')} checked={enabled} disabled={busy} onChange={(v) => void toggle(v)} />
      {denied && !enabled && <p style={{ margin: 0 }}>{t('settings.notificationsDenied')}</p>}
      <strong style={{ fontWeight: 800 }}>{t('settings.quietHours')}</strong>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
        <MinutePicker label={t('settings.quietFrom')} value={quiet.start} onChange={(m) => setQuiet(m, quiet.end)} />
        <MinutePicker label={t('settings.quietTo')} value={quiet.end} onChange={(m) => setQuiet(quiet.start, m)} />
      </div>
    </>
  );
}

function Credits() {
  const t = useT();
  const [licences, setLicences] = useState<KitLicence[] | null>(null);
  useEffect(() => {
    let live = true;
    fetch('/assets/LICENSES.json').then((r) => (r.ok ? (r.json() as Promise<KitLicence[]>) : [])).catch(() => [])
      .then((l) => { if (live) setLicences(Array.isArray(l) ? l : []); });
    return () => { live = false; };
  }, []);
  return (
    <>
      <p style={{ margin: 0 }}>{t('settings.licencesNote')}</p>
      {licences === null ? <p style={{ margin: 0 }}>{t('common.loading')}</p> : (
        <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
          {licences.map((l) => (
            <li key={l.kit}>
              <a href={l.source} target="_blank" rel="noreferrer" style={{ color: 'var(--sky-700)', fontWeight: 800 }}>{l.title}</a>
              {' · '}{l.author}{' · '}{l.licence}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Settings({ close }: { close: () => void }) {
  const t = useT();
  const cmd = useCommands();
  const settings = useGame((s) => s.settings);
  const email = useGame((s) => s.me?.user.email ?? null);
  const contact = t('settings.contact'), address = contactAddress(contact);
  // Two columns on a landscape phone (§8.7): device settings on the left, account, credits and help on the right.
  return (
    <Panel title={t('settings.title')}>
      <div style={{ display: 'grid', gap: '18px 26px', width: 720, maxWidth: '100%', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 18 }}>
          <Section title={t('settings.sound')}>
            <Slider label={t('settings.music')} value={settings.music} onChange={(music) => cmd.setVolumes({ music })} />
            <Slider label={t('settings.sfx')} value={settings.sfx} onChange={(sfx) => cmd.setVolumes({ sfx })} />
            <Toggle label={t('settings.haptics')} checked={settings.haptics} onChange={(haptics) => useGame.getState().setSettings({ haptics })} />
          </Section>
          <Section title={t('settings.notifications')}><Notifications /></Section>
          <Section title={t('settings.graphics')}>
            <Tabs tabs={QUALITIES.map((q) => ({ id: q.id, label: t(q.label) }))} active={settings.quality}
              onChange={(id) => cmd.setQuality(id as ClientSettings['quality'])} tight />
            <Toggle label={t('settings.perf')} checked={settings.perfOverlay} onChange={(perfOverlay) => useGame.getState().setSettings({ perfOverlay })} />
          </Section>
        </div>
        <div style={{ display: 'grid', gap: 18 }}>
          <Section title={t('install.title')}><InstallSettings /></Section>
          <Section title={t('settings.account')}>
            {email && <p style={{ margin: 0 }}>{t('settings.signedInAs', { email })}</p>}
            <Button variant="quiet" onClick={() => { cmd.leaveTown(); useGame.getState().safetyReset(); void useGame.getState().signOut(); }}>{t('settings.signOut')}</Button>
            <Button variant="quiet" onClick={() => useGame.getState().openScreen('blocked', undefined)}>{t('safety.settingsBlocked')}</Button>
            <Button variant="quiet" onClick={() => useGame.getState().openScreen('deleteAccount', undefined)}>{t('safety.settingsDelete')}</Button>
          </Section>
          <Section title={t('settings.licences')}><Credits /></Section>
          <Section title={t('settings.help')}>
            {address ? <a href={`mailto:${address}`} style={{ color: 'var(--sky-700)', fontWeight: 800 }}>{contact}</a> : <p style={{ margin: 0 }}>{contact}</p>}
          </Section>
          <Button variant="quiet" onClick={close}>{t('common.close')}</Button>
        </div>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'settings'> = { id: 'settings', layer: 'panel', component: Settings, menu: { label: 'settings.title', order: 90 } };
